<template>
    <div class="column-filter">
        <div v-if="loading" class="d-flex justify-content-center align-items-center py-4 h-100">
            <v-progress-circular color="primary" indeterminate></v-progress-circular>
        </div>
        <div v-else>
            <div v-if="filteredFilters.length" class="d-flex flex-wrap mt-2">
                <v-menu v-for="filter in filteredFilters" :key="filter.name" offset-y transition="slide-y-transition"
                    :close-on-content-click="false" :attach="true"
                    :nudge-left="menuNudges[filterMenuKey(filter)] || 0"
                    :content-class="`filter-menu-content filter-menu-content-${filterMenuKey(filter)}`"
                    @input="onMenuToggle(filter, $event)">
                    <template v-slot:activator="{ on, attrs }">
                        <div v-if="!loading && !hasSelectedValue(filter)" class="filter-item mr-2 mb-2" v-bind="attrs" v-on="on"
                            :ref="`filter-activator-${filterMenuKey(filter)}`">
                            <span>{{ getFilterLabel(filter) }}</span>
                            <v-icon small color="#3F3F3F">{{ filter.data_type === 'date' ? "mdi-calendar-blank-outline"
                                :
                                "mdi-chevron-down"
                            }}</v-icon>
                        </div>

                        <div v-else class="filter-item-selected mr-2 mb-2" v-bind="attrs" v-on="on"
                            :ref="`filter-activator-${filterMenuKey(filter)}`">
                            <div class="item-selected">
                                <span>{{ getFilterLabel(filter) }}</span>
                                <div class="d-flex justify-space-between">
                                    <h5>
                                        <b v-if="Array.isArray(filterValues[filter.keys[0]])">
                                            <span class="badge badge-primary mr-1"
                                                style="line-height: inherit; color: #FFF;">
                                                {{ filterValues[filter.keys[0]].length }}
                                            </span> Selected
                                        </b>
                                        <b v-else>
                                            {{ getSelectedDisplayValue(filter) }}
                                        </b>
                                    </h5>
                                    <v-icon small color="#3F3F3F">{{ filter.data_type === 'date' ?
                                        "mdi-calendar-blank-outline" :
                                        "mdi-chevron-down" }}</v-icon>
                                </div>
                            </div>
                            <div class="clear-filter" @click.stop="clearFilterValue(filter)">
                                <v-icon small color="#3F3F3F">mdi-close</v-icon>
                            </div>
                        </div>
                    </template>
                    <div id="attach-parent"></div>
                    <v-card class="px-4 py-2 filter-card" :class="{ 'date-filter-card': filter.data_type === 'date' }"
                        style="border-radius: 8px !important; max-width: 332px;">
                        <b class="mb-2 d-block" style="font-size: 14px;">{{ getFilterLabel(filter) }}</b>
                        <DynamicDateFilter v-if="filter.data_type === 'date'" :filter="filter"
                            :radio-value="getDateFilterRadioValue(filter)"
                            :presets="getDynamicPresetsForFilter(filter)"
                            :dynamic-value="getDynamicFilterValue(filter)"
                            :has-custom-config="Boolean(dynamicDateCustomConfig[filter.keys[0]])"
                            :custom-value="getCustomDynamicValue(filter)"
                            :custom-unit="getCustomDynamicUnit(filter)"
                            :custom-max="getCustomDynamicMax(filter)"
                            :unit-options="dynamicDateUnitOptions"
                            :start-value="filterValues[filter.keys[0]]"
                            :end-value="filter.keys[1] ? filterValues[filter.keys[1]] : ''"
                            :reference-date="referenceDate"
                            note-text="Dynamic date filters automatically update based on the scheduled send date. Exported data may change each time the report is sent."
                            @radio-change="setDateFilterRadioValue"
                            @preset-select="selectDynamicPreset"
                            @custom-value-input="setCustomDynamicValue"
                            @custom-unit-input="setCustomDynamicUnit"
                            @start-date-input="$set(filterValues, filter.keys[0], $event)"
                            @end-date-input="filter.keys[1] && $set(filterValues, filter.keys[1], $event)" />

                        <!-- Multi-Checkbox -->
                        <div v-else-if="filter.data_type === 'checkbox'">
                            <!-- Grouped View (when parent has multiple values) -->
                            <div v-if="Array.isArray(filter.data) && isGroupedFilter(filter)">
                                <!-- Select All for grouped departments -->
                                <div class="custom-control custom-checkbox mb-2">
                                    <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                                        :checked="isAllDepartmentsSelected(filter)"
                                        @change="toggleAllDepartments(filter, $event.target.checked)"
                                        class="custom-control-input">
                                    <label :for="`${filter.keys[0]}-select-all`"
                                        class="custom-control-label font-weight-bold">
                                        All
                                    </label>
                                </div>
                                <div v-for="(group, groupId) in getGroupedData(filter)" :key="groupId" class="mb-3"
                                    v-if="group.items && group.items.length > 0">
                                    <div class="parent-group-header"><b>{{ group.parentName }}</b></div>
                                    <div v-for="option in group.items" :key="option.id || option.name"
                                        class="custom-control custom-checkbox ml-2">
                                        <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                                            v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                                            @change="handleCheckboxChange(filter.keys[0], option.id)">
                                        <label :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                                            {{ option.name || '-' }}
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <!-- Flat View (default) -->
                            <div v-else-if="Array.isArray(filter.data)">
                                <!-- Select All for Business Unit -->
                                <div v-if="filter.keys[0] === businessUnitKey"
                                    class="custom-control custom-checkbox mb-2">
                                    <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                                        :checked="isAllBusinessUnitsSelected(filter)"
                                        @change="toggleAllBusinessUnits(filter, $event.target.checked)"
                                        class="custom-control-input">
                                    <label :for="`${filter.keys[0]}-select-all`"
                                        class="custom-control-label font-weight-bold">
                                        All
                                    </label>
                                </div>
                                <!-- Select All for Department (when 1 BU selected - flat view) -->
                                <div v-else-if="filter.keys[0] === departmentKey"
                                    class="custom-control custom-checkbox mb-2">
                                    <input type="checkbox" :id="`${filter.keys[0]}-select-all`"
                                        :checked="isAllDepartmentsSelectedFlat(filter)"
                                        @change="toggleAllDepartmentsFlat(filter, $event.target.checked)"
                                        class="custom-control-input">
                                    <label :for="`${filter.keys[0]}-select-all`"
                                        class="custom-control-label font-weight-bold">
                                        All
                                    </label>
                                </div>
                                <div v-for="option in filteredData(filter)" :key="option.id || option.name"
                                    class="custom-control custom-checkbox"
                                    v-if="!(filter.keys[0] === businessUnitKey && (option.id === 'all' || option.id === 'any'))">
                                    <input type="checkbox" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                                        v-model="filterValues[filter.keys[0]]" class="custom-control-input"
                                        @change="handleCheckboxChange(filter.keys[0], option.id)">
                                    <label v-if="filter.keys[0] === 'sticker_label_ids'"
                                        :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label sticker"
                                        :style="{ backgroundColor: option.color }">
                                        {{ option.name || '-' }}
                                    </label>
                                    <label v-else-if="filter.keys[0] === 'template_ids' && option.id !== 'all'"
                                        :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label"
                                        :style="{ backgroundColor: option.color }">
                                        #{{ option.id }} {{ option.name || '-' }}
                                    </label>
                                    <label v-else :for="`${filter.keys[0]}-${option.id}`" class="custom-control-label">
                                        {{ option.name || '-' }}
                                    </label>
                                </div>
                            </div>
                            <v-autocomplete v-else v-model="filterValues[filter.keys[0]]"
                                :items="dynamicOptions[filter.keys[0]]"
                                :search-input.sync="searchInputs[filter.keys[0]]" :menu-props="{ attach: true }"
                                item-value="id" item-text="name" label="Search" append-icon="mdi-magnify" solo dense
                                flat hide-details hide-no-data multiple small-chips deletable-chips cache-items
                                @update:search-input="fetchFilterData(filter.keys[0])"
                                @change="searchInputs[filter.keys[0]] = ''"></v-autocomplete>
                        </div>

                        <!-- Select Dropdown -->
                        <div v-else-if="filter.data_type === 'select'" class="position-relative">
                            <div v-for="option in filteredData(filter)" :key="option.id ? option.id : option"
                                @click="filterValues[filter.keys[0]] = option.id ? option.id : option"
                                class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;" :class="{
                                    'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                                }">
                                {{ option.name_code_format ? option.name_code_format : option.name ? option.name :
                                    option }}
                            </div>
                        </div>

                        <!-- Radio Buttons -->
                        <div v-else-if="filter.data_type === 'radio_button'">
                            <div v-for="option in filteredData(filter)" :key="option.id ? option.id : option"
                                @click="filterValues[filter.keys[0]] = option.id ? option.id : option"
                                class="px-4 py-1 hover-light-blue" style="margin: 0 -16px 0 -16px;" :class="{
                                    'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                                }">
                                {{ option.name ? option.name : option }}
                            </div>
                        </div>

                        <!-- Radio -->
                        <div v-else-if="filter.data_type === 'radio'">
                            <div v-for="option in filteredData(filter)"
                                @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)"
                                :key="option.id ? option.id : option" class="px-4 py-1 hover-light-blue"
                                style="margin: 0 -16px 0 -16px;" :class="{
                                    'bg-blue-active': filterValues[filter.keys[0]] === (option.id ? option.id : option),
                                }">
                                <input type="radio" :id="`${filter.keys[0]}-${option.id}`" :value="option.id"
                                    v-model="filterValues[filter.keys[0]]" style="opacity:1"
                                    @click="handleRadioChecked(filter.keys[0], option.id ? option.id : option)">
                                {{ option.name ? option.name : option }}
                            </div>

                            <!-- custom start date -->
                            <div v-if="filterValues[filter.keys[0]] == 'custom'" class="d-flex" style="gap: 16px;">
                                <div>
                                    <label class="form-label mb-1" style="font-size: 12px;">Start Date</label>
                                    <CInput v-model="customStartDate" type="date" placeholder="mm/dd/yyyy"
                                        style="width: 142px !important;" />
                                </div>
                                <div>
                                    <label class="form-label mb-1" style="font-size: 12px;">End Date</label>
                                    <CInput v-model="customEndDate" type="date" placeholder="mm/dd/yyyy"
                                        style="width: 142px !important;" />
                                </div>
                            </div>
                        </div>

                        <!-- Default Text Input -->
                        <div v-else>
                            <input v-model="filterValues[filter.keys[0]]" type="text" class="form-control">
                        </div>
                    </v-card>
                </v-menu>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import CInput from '../c-input.vue';
import DynamicDateFilter from '../report_filters/DynamicDateFilter.vue';
import dynamicDateFilterMixin from '../../shared/dynamic-date-filter-mixin';
import _ from 'lodash';
import moment from 'moment';
import {
    isDynamicFilter,
    extractDynamicCustomConfig,
    getDynamicFilterLabel,
} from '../../shared/report-filter-helpers';

export default {
    name: 'ScheduledReportColumnFilter',
    components: { CInput, DynamicDateFilter },
    mixins: [dynamicDateFilterMixin],

    props: {
        filterOptions: {
            type: Array,
            default: () => []
        },
        savedFilters: {
            type: Object,
            default: () => ({})
        },
        organizationId: {
            type: [Number, String],
            default: null
        },
        managementBusinessDepartment: {
            type: Boolean,
            default: false
        },
        referenceDate: {
            type: Object,
            default: null
        }
    },

    data() {
        return {
            customStartDate: "",
            customEndDate: "",
            filterValues: {},
            searchInputs: {},
            dynamicOptions: {},
            loading: false,
            initialDeptLoaded: false,
            previousBuSelection: [],
            menuNudges: {},
            isInitializing: false,
            emitTimer: null,
        };
    },

    computed: {
        filters() {
            return this.filterOptions || [];
        },
        filteredFilters() {
            return this.filters.filter(filter => {
                if (filter.data_type === 'date') return true;

                if (filter.keys.includes(this.departmentKey)) {
                    // Hide department if no valid BU selected (exclude 'all' from valid selection)
                    const buValues = this.filterValues[this.businessUnitKey];
                    if (!buValues || !Array.isArray(buValues)) return false;
                    const validBuIds = buValues.filter(id => id !== 'all');
                    return validBuIds.length > 0;
                }
                if (filter.keys.includes(this.businessUnitKey)) {
                    return this.managementBusinessDepartment;
                }
                return true;
            });
        },
        businessUnitKey() {
            const hasBusinessUnit = this.filters.some(filter => filter.keys?.[0] === 'business_unit_id');
            const hasClientBusinessUnit = this.filters.some(filter => filter.keys?.[0] === 'client_business_unit_id');
            if (hasBusinessUnit) return 'business_unit_id';
            if (hasClientBusinessUnit) return 'client_business_unit_id';
            return 'business_unit_id';
        },
        departmentKey() {
            const hasDepartment = this.filters.some(filter => filter.keys?.[0] === 'department_id');
            const hasClientDepartment = this.filters.some(filter => filter.keys?.[0] === 'client_department_id');
            if (hasDepartment) return 'department_id';
            if (hasClientDepartment) return 'client_department_id';
            return 'department_id';
        },
        totalAppliedFilters() {
            const filters = this.filterValues || {};
            return Object.values(filters).reduce((count, value) => {
                if (Array.isArray(value)) {
                    const filtered = value.filter(v => v.toLowerCase?.() !== 'all');
                    return count + (filtered.length > 0 ? 1 : 0);
                } else if (typeof value === 'string') {
                    const val = value.trim().toLowerCase();
                    return count + (val !== '' && val !== 'all' ? 1 : 0);
                } else if (value !== null && value !== undefined) {
                    return count + 1;
                }
                return count;
            }, 0);
        },
    },

    methods: {
        getFilterLabel(filter) {
            if (['client_business_unit_id', 'client_department_id'].includes(filter.keys[0])) {
                return `${filter.name} (Client)`;
            }
            return filter.name;
        },
        async initializeFilterValues() {
            this.loading = true;
            this.isInitializing = true;
            this.customStartDate = "";
            this.customEndDate = "";
            this.initialDeptLoaded = false;
            this.previousBuSelection = [];
            this.menuNudges = {};
            this.dateFilterRadioValue = {};
            this.dynamicDateCustomConfig = {};
            this.dynamicDateFilterValues = this.normalizeDynamicFilters(this.savedFilters?.dynamic_filters);
            this.filterValues = { ...(this.savedFilters || {}) };
            this.$delete(this.filterValues, 'dynamic_filters');

            this.filters.forEach(filter => {
                const savedFilterValue = this.savedFilters?.[filter.keys[0]];

                if (filter.data_type === "checkbox") {
                    if (Array.isArray(savedFilterValue) && savedFilterValue.length > 0) {
                        this.$set(this.filterValues, filter.keys[0], this.expandAny(savedFilterValue, filter));
                    } else {
                        const allOption = filter.data?.find(option => option.id === "all" || option.id === "any");
                        if (!allOption) {
                            this.$set(this.filterValues, filter.keys[0], []);
                        } else if (allOption.id === "any") {
                            this.$set(this.filterValues, filter.keys[0], this.realOptionIds(filter.data));
                        } else {
                            this.$set(this.filterValues, filter.keys[0], ["all"]);
                        }
                    }
                } else if (filter.data_type === "radio") {
                    if (savedFilterValue) {
                        if (typeof savedFilterValue === "string" && savedFilterValue.includes("to")) {
                            const splitValueDate = savedFilterValue.split(" ");
                            this.customStartDate = splitValueDate[0];
                            this.customEndDate = splitValueDate[2];
                            this.$set(this.filterValues, filter.keys[0], "custom");
                        } else {
                            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
                        }
                    } else {
                        const allOption = filter.data?.find(option => option.id === "all");
                        this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
                    }
                } else if (filter.data_type === "radio_button") {
                    if (savedFilterValue) {
                        this.$set(this.filterValues, filter.keys[0], savedFilterValue);
                    } else {
                        const allOption = filter.data?.find(option => option.id === "all");
                        this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
                    }
                } else if (filter.data_type === "select") {
                    if (savedFilterValue) {
                        this.$set(this.filterValues, filter.keys[0], savedFilterValue);
                    } else {
                        const allOption = filter.data?.find(option => option.id === "all");
                        this.$set(this.filterValues, filter.keys[0], allOption ? "all" : "");
                    }
                } else if (filter.data_type === "date" && filter.keys.length > 1) {
                    const savedDynamicValue = this.getDynamicFilterValue(filter);
                    if (savedDynamicValue) {
                        this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
                        const customConfig = extractDynamicCustomConfig(savedDynamicValue);
                        if (customConfig) {
                            this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
                        }
                        this.$set(this.filterValues, filter.keys[0], "");
                        this.$set(this.filterValues, filter.keys[1], "");
                    } else if (savedFilterValue) {
                        if (isDynamicFilter(savedFilterValue)) {
                            this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
                            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
                            const customConfig = extractDynamicCustomConfig(savedFilterValue);
                            if (customConfig) {
                                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
                            }
                            this.$set(this.filterValues, filter.keys[0], "");
                            this.$set(this.filterValues, filter.keys[1], "");
                        } else {
                            this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
                            this.$set(this.filterValues, filter.keys[0], savedFilterValue);
                            this.$set(this.filterValues, filter.keys[1], this.savedFilters?.[filter.keys[1]] || "");
                        }
                    } else {
                        this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
                        this.$set(this.filterValues, filter.keys[0], "");
                        this.$set(this.filterValues, filter.keys[1], "");
                    }
                } else if (filter.data_type === "date") {
                    const savedDynamicValue = this.getDynamicFilterValue(filter);
                    if (savedDynamicValue) {
                        this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
                        const customConfig = extractDynamicCustomConfig(savedDynamicValue);
                        if (customConfig) {
                            this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
                        }
                        this.$set(this.filterValues, filter.keys[0], "");
                    } else if (savedFilterValue) {
                        if (isDynamicFilter(savedFilterValue)) {
                            this.$set(this.dynamicDateFilterValues, filter.name, savedFilterValue);
                            this.$set(this.dateFilterRadioValue, filter.keys[0], 'dynamic');
                            const customConfig = extractDynamicCustomConfig(savedFilterValue);
                            if (customConfig) {
                                this.$set(this.dynamicDateCustomConfig, filter.keys[0], customConfig);
                            }
                        } else {
                            this.$set(this.dateFilterRadioValue, filter.keys[0], 'absolute');
                        }
                    } else {
                        this.$set(this.dateFilterRadioValue, filter.keys[0], 'empty');
                    }
                    this.$set(this.filterValues, filter.keys[0], (savedDynamicValue || isDynamicFilter(savedFilterValue)) ? "" : (savedFilterValue ?? ""));
                } else {
                    this.$set(this.filterValues, filter.keys[0], savedFilterValue ?? "");
                }

                if (!filter.data) {
                    this.$set(this.dynamicOptions, filter.keys[0], []);
                    this.$set(this.searchInputs, filter.keys[0], "");
                }
            });

            // Preload dynamic options (e.g., contributors/clients) so dropdowns render on open
            // and mirror Template Builder behavior without requiring a search query first.
            await this.fetchInitialDynamicOptions();

            if (this.filterValues[this.businessUnitKey]) {
                this.initialDeptLoaded = false;
                this.handleBusinessUnitChange(this.filterValues[this.businessUnitKey]);
            }

            this.isInitializing = false;
            this.emitFilterValues();
            this.loading = false;
        },

        fetchFilterData: _.debounce(async function (key) {
            const query = this.searchInputs[key]?.trim();
            if (!query) return;
            await this.fetchDynamicOptionsForKey(key, query);
        }, 500),

        async fetchInitialDynamicOptions() {
            const reportListState = this.$store?.state?.reportList || {};
            const rootState = this.$store?.state || {};
            const apiEndpoints = reportListState.apiEndpoints || {};

            const dynamicKeys = this.filters
                .filter(filter => !Array.isArray(filter.data))
                .map(filter => filter.keys?.[0])
                .filter(Boolean)
                .filter(key => apiEndpoints[key]);

            if (dynamicKeys.length === 0) return;

            await Promise.all(
                dynamicKeys.map(key => this.fetchDynamicOptionsForKey(key))
            );
        },

        async fetchDynamicOptionsForKey(key, query = '') {
            const reportListState = this.$store?.state?.reportList || {};
            const rootState = this.$store?.state || {};
            const token = reportListState.authorization?.token || rootState?.authorization?.token;
            const organizationId = reportListState.organizationId || rootState?.organization?.id || rootState?.organizationId;
            const endpointTemplate = reportListState.apiEndpoints?.[key];

            if (!endpointTemplate) return;
            if (endpointTemplate.includes(":organization_id") && !organizationId) return;

            const endpoint = endpointTemplate.replace(":organization_id", organizationId);
            const selectedIds = this.savedFilters?.[key];
            const params = {
                query,
                organization_id: organizationId,
            };

            if (Array.isArray(selectedIds) && selectedIds.length > 0) {
                params.sort_by_user_ids = selectedIds.join(',');
            }

            try {
                const response = await axios.get(endpoint, {
                    params,
                    headers: token ? { Authorization: token } : {},
                });
                this.$set(this.dynamicOptions, key, response.data || []);
            } catch (error) {
                console.error(`Error fetching ${key}:`, error);
            }
        },
        filterMenuKey(filter) {
            const rawKey = filter?.keys?.[0] || 'filter';
            return rawKey.toString().replace(/[^a-z0-9_-]/gi, '-');
        },
        onMenuToggle(filter, isOpen) {
            if (!isOpen) return;
            const key = this.filterMenuKey(filter);
            if (!key) return;

            this.$nextTick(() => {
                this.updateMenuNudge(key);
                if (typeof requestAnimationFrame !== 'undefined') {
                    requestAnimationFrame(() => this.updateMenuNudge(key));
                }
                setTimeout(() => this.updateMenuNudge(key), 60);
            });
        },
        updateMenuNudge(key) {
            const activatorRef = this.$refs[`filter-activator-${key}`];
            const activatorEl = Array.isArray(activatorRef) ? activatorRef[0] : activatorRef;
            const contentEl = document.querySelector(`.filter-menu-content-${key}`);
            if (!activatorEl || !contentEl) return;

            const activatorRect = activatorEl.getBoundingClientRect();
            const menuRect = contentEl.getBoundingClientRect();
            if (!menuRect.width) return;

            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const desiredPadding = 16;

            // Constrain menu width to the viewport
            const maxWidth = Math.max(240, viewportWidth - desiredPadding * 2);
            contentEl.style.maxWidth = `${maxWidth}px`;
            contentEl.style.right = 'auto';

            // Keep menu aligned to activator unless it would overflow right
            const maxLeft = viewportWidth - menuRect.width - desiredPadding;
            const nextLeft = Math.max(desiredPadding, Math.min(activatorRect.left, maxLeft));
            contentEl.style.left = `${nextLeft}px`;

            // Disable extra nudge (we control positioning directly)
            this.$set(this.menuNudges, key, 0);
        },

        isGroupedFilter(filter) {
            // Check if filter has parent config and parent has multiple values selected
            if (!filter.parent_key || !filter.parent_filter_key) return false;
            const parentValues = this.filterValues[filter.parent_filter_key];
            return Array.isArray(parentValues) && parentValues.length > 1;
        },

        getGroupedData(filter) {
            if (!filter.parent_key || !filter.parent_filter_key) return {};

            const parentFilterKey = filter.parent_filter_key;
            const parentKey = filter.parent_key;
            const parentValues = this.filterValues[parentFilterKey] || [];

            // Get parent filter to get parent names
            const parentFilter = this.filters.find(f => f.keys[0] === parentFilterKey);
            const parentNameMap = {};
            if (parentFilter && Array.isArray(parentFilter.data)) {
                parentFilter.data.forEach(item => {
                    parentNameMap[item.id] = item.name;
                });
            }

            // Get data source (dynamic or static)
            let data;
            if (Array.isArray(this.dynamicOptions[filter.keys[0]])) {
                data = this.dynamicOptions[filter.keys[0]];
            } else {
                data = filter.data || [];
            }

            // Filter by selected parent values and exclude "all"
            const filteredData = data.filter(item =>
                item?.id !== "all" && item?.id !== "any" && parentValues.includes(item[parentKey])
            );

            // Group by parent key
            const grouped = {};
            filteredData.forEach(item => {
                const parentId = item[parentKey];
                if (!grouped[parentId]) {
                    grouped[parentId] = {
                        parentName: parentNameMap[parentId] || `Group ${parentId}`,
                        items: []
                    };
                }
                grouped[parentId].items.push(item);
            });
            return grouped;
        },

        isAllDepartmentsSelected(filter) {
            const groupedData = this.getGroupedData(filter);
            const allDeptIds = [];
            Object.values(groupedData).forEach(group => {
                group.items.forEach(item => allDeptIds.push(item.id));
            });
            const selectedIds = this.filterValues[filter.keys[0]] || [];
            return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
        },

        toggleAllDepartments(filter, checked) {
            const groupedData = this.getGroupedData(filter);
            const allDeptIds = [];
            Object.values(groupedData).forEach(group => {
                group.items.forEach(item => allDeptIds.push(item.id));
            });
            if (checked) {
                this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
            } else {
                this.$set(this.filterValues, filter.keys[0], []);
            }
            this.emitFilterValues();
        },

        isAllBusinessUnitsSelected(filter) {
            const data = filter.data || [];
            const allBuIds = data.filter(item => item.id !== 'all' && item.id !== 'any').map(item => item.id);
            const selectedIds = this.filterValues[filter.keys[0]] || [];
            return allBuIds.length > 0 && allBuIds.every(id => selectedIds.includes(id));
        },

        toggleAllBusinessUnits(filter, checked) {
            const data = filter.data || [];
            const allBuIds = data.filter(item => item.id !== 'all' && item.id !== 'any').map(item => item.id);
            if (checked) {
                this.$set(this.filterValues, filter.keys[0], [...allBuIds]);
            } else {
                this.$set(this.filterValues, filter.keys[0], []);
            }
            this.emitFilterValues();
        },

        isAllDepartmentsSelectedFlat(filter) {
            const data = this.dynamicOptions[this.departmentKey] || [];
            const allDeptIds = data.filter(item => item.id !== 'all' && item.id !== 'any').map(item => item.id);
            const selectedIds = this.filterValues[filter.keys[0]] || [];
            return allDeptIds.length > 0 && allDeptIds.every(id => selectedIds.includes(id));
        },

        toggleAllDepartmentsFlat(filter, checked) {
            const data = this.dynamicOptions[this.departmentKey] || [];
            const allDeptIds = data.filter(item => item.id !== 'all' && item.id !== 'any').map(item => item.id);
            if (checked) {
                this.$set(this.filterValues, filter.keys[0], [...allDeptIds]);
            } else {
                this.$set(this.filterValues, filter.keys[0], []);
            }
            this.emitFilterValues();
        },

        filteredData(filter) {
            let data;

            if (filter.keys[0] === this.departmentKey && Array.isArray(this.dynamicOptions[this.departmentKey])) {
                data = this.dynamicOptions[this.departmentKey];
            } else {
                data = filter.data;
            }

            return (data || []).filter(option => option?.id !== "all" && option?.id !== "any");
        },
        // --- "any" sentinel helpers (BU/Department "All") ---
        realOptionIds(list) {
            return (list || []).filter(o => o && o.id !== "all" && o.id !== "any").map(o => o.id);
        },
        expandAny(value, filter) {
            if (Array.isArray(value) && value.length === 1 && value[0] === "any") {
                return this.realOptionIds(filter.data);
            }
            return value;
        },
        collapseToAny(filters, key, available) {
            const ids = this.realOptionIds(available);
            const selected = filters[key] || [];
            if (ids.length > 0 && ids.every(id => selected.includes(id))) {
                filters[key] = ["any"];
            }
        },

        handleRadioChecked(filterKey, option) {
            if (this.filterValues[filterKey] !== undefined) {
                this.filterValues[filterKey] = option;
                this.emitFilterValues();
            }
        },

        handleCheckboxChange(filterKey, selectedId) {
            if (!this.filterValues[filterKey]) {
                this.$set(this.filterValues, filterKey, []);
            }

            let selectedValues = this.filterValues[filterKey];

            if (selectedId === "all") {
                this.$set(this.filterValues, filterKey, ["all"]);
            } else {
                this.filterValues[filterKey] = selectedValues.filter(id => id !== "all");
            }

            this.emitFilterValues();
        },

        emitFilterValues() {
            const processedFilters = Object.fromEntries(
                Object.entries(this.filterValues).map(([key, value]) => {
                    if (key === 'dynamic_filters') return null;
                    if (Array.isArray(value)) {
                        const filteredValue = value.filter(item => item !== "all");
                        return filteredValue.length > 0 ? [key, filteredValue] : [key, []];
                    } else if (typeof value === "string") {
                        if (value === "all") {
                            return [key, ""];
                        } else if (value === "custom") {
                            if (this.customStartDate && this.customEndDate) {
                                const startDateCustom = moment(this.customStartDate).format("YYYY-MM-DD");
                                const endDateCustom = moment(this.customEndDate).format("YYYY-MM-DD");
                                return [key, `${startDateCustom} to ${endDateCustom}`];
                            }
                            return [key, ""];
                        }
                    }
                    return [key, value];
                }).filter(Boolean)
            );

            // Drop half-filled static date ranges so the preview isn't skewed
            this.filters.filter(f => f.data_type === 'date' && f.keys.length > 1).forEach(f => {
                const [startKey, endKey] = f.keys;
                if (this.dateFilterRadioValue[startKey] === 'absolute'
                    && (!processedFilters[startKey] || !processedFilters[endKey])) {
                    delete processedFilters[startKey];
                    delete processedFilters[endKey];
                }
            });

            const dynamicFilters = this.buildDynamicFiltersPayload();
            processedFilters.dynamic_filters = dynamicFilters;

            this.collapseToAny(processedFilters, this.businessUnitKey, this.filters.find(f => f.keys[0] === this.businessUnitKey)?.data);
            this.collapseToAny(processedFilters, this.departmentKey, this.dynamicOptions[this.departmentKey]);

            this.$emit('update:filters', processedFilters);
        },

        scheduleEmitFilterValues() {
            if (this.isInitializing || this.emitTimer) return;
            this.emitTimer = Promise.resolve().then(() => {
                this.emitTimer = null;
                this.emitFilterValues();
            });
        },

        hasSelectedValue(filter) {
            const key = filter.keys[0];
            const value = this.filterValues[key];

            if (filter.data_type === 'date') {
                if (this.getDynamicFilterValue(filter) || isDynamicFilter(value)) {
                    return true;
                }
                // For absolute dates, require both start and end
                if (filter.keys.length > 1) {
                    return this.filterValues[filter.keys[0]] && this.filterValues[filter.keys[1]];
                }
                return value && value !== "";
            }

            if (Array.isArray(value)) {
                return value.length > 0 && !(value.length === 1 && value[0] === "all");
            }
            return value && value !== "all" && value !== "";
        },

        getSelectedDisplayValue(filter) {
            const key = filter.keys[0];
            const value = this.filterValues[key];

            if (filter.data_type === 'date') {
                const dynamicValue = this.getDynamicFilterValue(filter);
                if (dynamicValue) {
                    return getDynamicFilterLabel(dynamicValue, this.getDynamicPresetsForFilter(filter));
                }
                if (isDynamicFilter(value)) {
                    return getDynamicFilterLabel(value, this.getDynamicPresetsForFilter(filter));
                }
                if (filter.keys.length === 1) {
                    return this.formatDate(value);
                }
                // Absolute date range
                const start = this.filterValues[filter.keys[0]];
                const end = this.filterValues[filter.keys[1]];
                return `${this.formatDate(start)} - ${this.formatDate(end)}`;
            }

            if (Array.isArray(value)) {
                return `${value.length} selected`;
            }

            if (filter.data_type === 'select' || filter.data_type === 'radio') {
                const option = (filter.data || []).find(opt => opt.id === value || opt === value);
                return option?.name || option?.name_code_format || value;
            }

            return value;
        },

        formatDate(dateStr) {
            if (!dateStr) return "";
            return moment(dateStr, "YYYY-MM-DD", true).format("M/D/YYYY");
        },

        clearFilterValue(filter) {
            const key = filter.keys[0];
            if (filter.data_type === 'checkbox') {
                this.$set(this.filterValues, key, []);
            } else if (filter.data_type === 'date' && filter.keys.length > 1) {
                // Reset to empty state
                this.$set(this.dateFilterRadioValue, key, 'empty');
                this.$set(this.filterValues, filter.keys[0], "");
                this.$set(this.filterValues, filter.keys[1], "");
                // Clear custom config
                if (this.dynamicDateCustomConfig[key]) {
                    this.$delete(this.dynamicDateCustomConfig, key);
                }
                this.$delete(this.dynamicDateFilterValues, filter.name);
            } else if (filter.data_type === 'date') {
                this.$set(this.dateFilterRadioValue, key, 'empty');
                this.$set(this.filterValues, key, "");
                if (this.dynamicDateCustomConfig[key]) {
                    this.$delete(this.dynamicDateCustomConfig, key);
                }
                this.$delete(this.dynamicDateFilterValues, filter.name);
            } else {
                this.$set(this.filterValues, key, "");
            }
            this.emitFilterValues();
        },

        handleBusinessUnitChange(newBusinessUnitId) {
            const departmentFilter = this.filters.find(f => f.keys[0] === this.departmentKey);
            if (!(departmentFilter && Array.isArray(departmentFilter.data))) return;

            const selectedBuIds = Array.isArray(newBusinessUnitId)
                ? newBusinessUnitId.filter(id => id !== 'all' && id !== 'any').map(id => id.toString())
                : [];

            if (selectedBuIds.length === 0) {
                this.$set(this.dynamicOptions, this.departmentKey, []);
                this.$set(this.filterValues, this.departmentKey, []);
                this.previousBuSelection = [];
                return;
            }

            const allDepartments = (departmentFilter.data || []).filter(dept => dept?.id !== 'all' && dept?.id !== 'any');
            const filteredDepartments = allDepartments.filter(dept =>
                selectedBuIds.includes(dept.business_unit_id?.toString())
            );

            this.$set(this.dynamicOptions, this.departmentKey, filteredDepartments);

            // On initial load, prioritize saved filter over auto-select
            const savedDeptIds = this.savedFilters?.[this.departmentKey];
            if (!this.initialDeptLoaded && Array.isArray(savedDeptIds) && savedDeptIds.length > 0) {
                const validSavedDepartments = savedDeptIds.filter(id =>
                    filteredDepartments.some(dept => dept.id === id)
                );
                if (validSavedDepartments.length > 0) {
                    this.$set(this.filterValues, this.departmentKey, validSavedDepartments);
                    this.initialDeptLoaded = true;
                    this.previousBuSelection = [...selectedBuIds];
                    return;
                }
            }

            const nextDeptIds = filteredDepartments.map(dept => dept.id);
            this.$set(this.filterValues, this.departmentKey, nextDeptIds);

            this.previousBuSelection = [...selectedBuIds];
            this.initialDeptLoaded = true;
        },

    },

    watch: {
        filterOptions: {
            handler(newFilters) {
                if (newFilters && newFilters.length > 0) {
                    this.initializeFilterValues();
                }
            },
            immediate: true,
            deep: true
        },

        filterValues: {
            handler() {
                this.scheduleEmitFilterValues();
            },
            deep: true
        },

        dynamicDateFilterValues: {
            handler() {
                this.scheduleEmitFilterValues();
            },
            deep: true
        },

        customStartDate() {
            this.emitFilterValues();
        },

        customEndDate() {
            this.emitFilterValues();
        },

        "filterValues.client_business_unit_id": {
            handler(newVal) {
                if (this.businessUnitKey === 'client_business_unit_id') {
                    this.handleBusinessUnitChange(newVal);
                }
            },
            immediate: true
        },
        "filterValues.business_unit_id": {
            handler(newVal) {
                if (this.businessUnitKey === 'business_unit_id') {
                    this.handleBusinessUnitChange(newVal);
                }
            },
            immediate: true
        },
    },
};
</script>


<style lang="scss" scoped>
.column-filter {
        .filter-item {
        background-color: #FFF;
        border: 1px solid #D7D7D7;
        border-radius: 8px;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        height: 48px;
        cursor: pointer;

        &:hover {
            background-color: #F5F5F5;
        }
    }

    .filter-item-selected {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 2px;
        height: 48px;
    }

        .item-selected {
        border-radius: 8px 0 0 8px;
        background-color: #D4E7FF;
        padding: 0 8px 0 12px;
        height: 100%;
        min-width: 132px;
        max-width: max-content;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .item-selected span {
        font-size: 9px;
        color: #848484;
        text-transform: uppercase;
        font-weight: bold;
    }

    .item-selected h5 {
        font-size: 14px;
        color: #3F3F3F;
        margin: 0;
        margin-right: 6px;
    }

    .clear-filter {
        border-radius: 0 8px 8px 0;
        background-color: #D4E7FF;
        width: 32px;
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;

        &:hover {
            background-color: #C0D9F7;
        }
    }

    .bg-blue-active {
        background-color: #EDF4FE !important;
    }

    .parent-group-header {
        font-weight: bold;
        font-size: 13px;
        color: #3F3F3F;
        padding: 8px 0 4px 0;
        border-bottom: 1px solid #E0E0E0;
        margin-bottom: 8px;
    }

}

::v-deep.v-list-item__action {
    margin-right: 10px !important;
}

::v-deep .filter-card {
    max-height: 500px;
    overflow: auto;
}

::v-deep .filter-card.date-filter-card {
    width: 370px;
    max-width: min(370px, calc(100vw - 32px)) !important;
}

::v-deep .filter-menu-content {
    z-index: 2000 !important;
}

::v-deep .filter-card .sticker {
    padding: 8px 16px;
    border-radius: 4px;
    margin-bottom: 8px;
}

::v-deep .custom-control-label.sticker::before,
.custom-control-label.sticker::after {
    top: 0.7rem !important;
}

::v-deep .v-select__selections {
    gap: 6px 0;
    padding: 8px 0;
}
</style>

<style>
.bg-blue-active {
    background-color: #EDF4FE !important;
}

.hover-light-blue:hover {
    background-color: #EDF4FE !important;
    cursor: pointer !important;
}
</style>
